import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScreenTitle from '../../common/components/screenTitle/ScreenTitle';
import Box from 'common/components/box/Box';
import styles from './VehiclesScreen.module.scss';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import { DATE_FORMAT_MONTH_YEAR, DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE, POLICIES } from 'Config';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { useHistory } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import VehiclesService from 'api/vehicles/VehiclesService';
import { VehicleViewModel } from 'api/vehicles/models/VehicleViewModel';
import Popover from 'common/components/popover/Popover';
import VehicleFiltersScreen, { Filters } from './components/VehicleFiltersScreen';
import { VehiclesSearchCriteria } from 'api/vehicles/models/VehiclesSearchCriteria';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import DateFormat from 'common/components/dateFormat/dateFormat';
import VehicleCard from './components/vehicleCard/VehicleCard';
import { useDebouncedCallback } from 'use-debounce';
import InputSearch from 'common/components/inputSearch/InputSearch';
import Loading from 'common/services/Loading';
import HelperModal from 'common/components/helperModal/HelperModal';
import InfoIcon from 'assets/svg/i_info_primaryDark.svg';
import ImportIcon from 'assets/svg/file-arrow-up-solid.svg';
import InfoTour from 'common/components/info-tour/InfoTour';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import TrackingService from 'api/tracking/TrackingService';
import CompaniesService from 'api/companies/CompaniesService';
import { TrackingItemDto } from 'api/tracking/models/TrackingItemDto';
import { FaTh, FaList } from 'react-icons/fa';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import { CompactPageLayout } from 'common/components/compactPageLayout';
import VehicleListTable from './components/VehicleListTable/VehicleListTable';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import useResponsiveGridPageSize from 'common/hooks/useResponsiveGridPageSize';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';

type VehiclesListTourItem = {
    selector: string;
    title: string;
    text: string;
    position?: string;
    policies: (keyof typeof POLICIES)[];
    type?: 'OR' | 'AND';
};


const VehiclesScreen = () => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const [trackingVehicles, setTrackingVehicles] = useState<TrackingItemDto[] | null>(null);
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasCountersPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CHARGES_TOTAL_READ']);
    const hasVehiclesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLES_WRITE']);
    const hasImportsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_IMPORT_DATA_WRITE']);
    const hasGpsPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_GPS_READ']);
    const hasPrivacyStatusPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_PRIVACY_STATUS_READ']);
    const hasAlertsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_ALERTS_READ']);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    // Page size dinâmico para grid view (baseado na largura do container)
    const { pageSize: responsivePageSize, containerRef: cardsContainerRef } = useResponsiveGridPageSize();

    const [criteria, setCriteria] = useState<VehiclesSearchCriteria>({
        itemsPerPage: responsivePageSize,
        page: 1,
        orderColumn: 'v.registration_number',
        orderBy: 'asc',
    });
    const [vehicles, setVehicles] = useState<VehicleViewModel[]>([]);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
        const saved = localStorage.getItem('vehiclesViewMode');
        return (saved === 'list' || saved === 'grid') ? saved : 'grid';
    });

    const [pageLoaded, setPageLoaded] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);
    const [tourOpen, setTourOpen] = useState<boolean>(false);
    const [companyHaveVehicles, setCompanyHaveVehicles] = useState<boolean | null>(null);
    const [companyHasGpsLocators, setCompanyHasGpsLocators] = useState<boolean>(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [privacyStatusMap, setPrivacyStatusMap] = useState<Record<string, import('api/tracking/TrackingService').PrivacyStatus>>({});
    const skipNextFetch = useRef(false);
    // Assinatura do último fetch efetivo — para deduplicar o duplo do arranque
    // (page-size responsivo a assentar + loadFilters mudam o criteria 2x para o mesmo fetch).
    const lastFetchKeyRef = useRef<string>('');

    // Bidirectional scroll hook
    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: 'VEHICLESFILTER',
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        isScrollRestoring,
        scrollToRowId: scrollToVehicleId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    const validateItem = useCallback((item: VehiclesListTourItem) => {
        const type = item.type || 'AND';
        const policies = item.policies || [];
        const userPolicies = userProfile?.policies || [];
        return !policies.length || UsersService.hasPolicies(userPolicies, policies, type);
    }, [userProfile]);

    const steps: VehiclesListTourItem[] = useMemo(() => {
        const items: VehiclesListTourItem[] = [
            {
                selector: '#first-step',
                title: t('vehicles.tour.car_list'),
                text: t('vehicles.tour.car_list_message'),
                policies: [],
            },
            {
                selector: '#second-step',
                title: t('vehicles.tour.search_by_text'),
                text: t('vehicles.tour.search_by_text_message'),
                policies: [],
            },
            {
                selector: '#third-step',
                title: t('vehicles.tour.filters'),
                text: t('vehicles.tour.filters_message'),
                policies: [],
                position: 'top'
            },
            {
                selector: '#four-step',
                title: t('vehicles.tour.import'),
                text: t('vehicles.tour.import_message'),
                policies: ['SETTINGUP_IMPORT_DATA_WRITE'],
                type: 'OR',
            },
            {
                selector: '#five-step',
                title: t('vehicles.tour.new_car'),
                text: t('vehicles.tour.new_car_message'),
                policies: ['SETTINGUP_VEHICLES_WRITE'],
                type: 'OR',
            },
        ];

        return items.filter(item => validateItem(item));
    }, [userProfile, validateItem]);

    const getData = async (isLoadMore = false) => {
        try {
            // Usar o responsivePageSize diretamente para grid view
            const effectiveItemsPerPage = viewMode === 'grid' ? responsivePageSize : DEFAULT_PAGINATION_ITEMS_PER_PAGE;
            const effectiveCriteria = { ...criteria, itemsPerPage: effectiveItemsPerPage };

            // Dedupe por VALOR: no arranque o criteria muda de referência 2x (page-size responsivo a
            // assentar + loadFilters), mas o pedido efetivo é o mesmo. Só refazer se o pedido mudou.
            // Reseta por-mount (ref novo a cada montagem), por isso voltar da ficha recarrega na mesma.
            const fetchKey = JSON.stringify(effectiveCriteria);
            if (!isLoadMore && lastFetchKeyRef.current === fetchKey) {
                return;
            }
            lastFetchKeyRef.current = fetchKey;

            if (!isLoadMore) {
                setTrackingVehicles(null);
            }

            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            const result = await VehiclesService.companyHaveVehicles();
            setCompanyHaveVehicles(result);

            const vehiclesDB = await VehiclesService.getList(effectiveCriteria);
            setTotalItems(vehiclesDB.totalItems);

            if (isLoadMore) {
                setVehicles(prev => [...prev, ...vehiclesDB.items]);
                // Update loadedPageRange.max after successful load
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setVehicles(vehiclesDB.items);
            }

            Loading.hide();
            setIsLoadingMore(false);

            // Collect all items for GPS loading (including adjacent pages)
            let allItemsForGps: VehicleViewModel[] = [...vehiclesDB.items];

            // When restoring scroll, load adjacent pages (previous and next) to have enough content to scroll
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = effectiveCriteria.page;
                const totalPages = Math.ceil(vehiclesDB.totalItems / effectiveItemsPerPage);

                // Load previous page if exists
                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...effectiveCriteria, page: currentPage - 1 };
                        const prevResult = await VehiclesService.getList(prevCriteria);
                        setVehicles(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                        // Add to GPS loading list
                        allItemsForGps = [...prevResult.items, ...allItemsForGps];
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't load previous page`, e);
                    }
                }

                // Load next page if exists
                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...effectiveCriteria, page: currentPage + 1 };
                        const nextResult = await VehiclesService.getList(nextCriteria);
                        setVehicles(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                        // Add to GPS loading list
                        allItemsForGps = [...allItemsForGps, ...nextResult.items];
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't load next page`, e);
                    }
                }

                // Trigger scroll to the target vehicle
                finishRestoration();
            }

            // Load GPS tracking for all loaded vehicles (including adjacent pages)
            if (hasGpsPolicy && allItemsForGps.length) {
                const company = await CompaniesService.getCurrent();
                const hasGpsLocators = !!(company && company.gpsLocators && company.gpsLocators.length > 0);
                setCompanyHasGpsLocators(hasGpsLocators);

                if (hasGpsLocators) {
                    try {
                        const ids = allItemsForGps.filter(x => x.trackingDeviceId != null && x.id != undefined).map(x => x.id as string) ?? [];
                        if (ids.length) {
                            const res = await TrackingService.getGpsVehiclesPosition(ids);
                            // Always merge by vehicle ID to avoid race conditions when multiple pages load simultaneously
                            setTrackingVehicles(prev => {
                                if (!prev) return res;
                                // Create a map of existing tracking data by vehicle ID
                                const existingMap = new Map(prev.map(t => [t.vehicleId, t]));
                                // Add/update with new data
                                res.forEach(t => existingMap.set(t.vehicleId, t));
                                return Array.from(existingMap.values());
                            });
                        }
                    } catch (err) {
                        Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get vehicles tracking list`, err);
                        addToast(t('common.messages.error_load_info'), { appearance: 'error' });
                    }
                }
            }

        } catch (error) {
            Loading.hide();
            setIsLoadingMore(false);
            resetScrollState();
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get vehicles list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    const navigateTo = (type?: string, id?: string, gps?: boolean) => {
        if (id) {
            history.push(`/vehicles/${type}/${id}` + (gps ? '/gps' : ''));
        } else {
            history.push(`/vehicles/${type}`);
        }
    };

    const onCardClick = (event: any, id?: string, gps?: boolean) => {
        if (id) {
            event.preventDefault();
            event.stopPropagation();

            // Calculate the actual page of the clicked vehicle based on its position in the list
            const vehicleIndex = vehicles.findIndex(v => v.id === id);
            const actualPage = calculatePageFromIndex(vehicleIndex);

            filters.page = actualPage;
            filters.scrollToVehicleId = id;
            saveFiltersOnCache(filters);
            if (event.ctrlKey) {
                window.open(history.location.pathname + '/details/' + id + (gps ? '/gps' : ''), '_blank');
                return;
            }
            navigateTo('details', id, gps);
        }
    };

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }));
        localStorage.setItem('VEHICLESFILTER', JSON.stringify(f));
    };

    const onPageChange = (page: number) => {
        setCriteria({ ...criteria, page });
    };

    const loadMore = useCallback(() => {
        if (isLoadingMore || vehicles.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, vehicles.length, totalItems, loadedPageRange.max]);

    // Load previous pages (bidirectional scroll)
    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            // Store scroll height before prepending
            prepareLoadPrevious();

            const prevCriteria: VehiclesSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await VehiclesService.getList(prevCriteria);

            // Prepend items to the list
            setVehicles(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));

            // Load GPS tracking for prepended vehicles
            if (hasGpsPolicy && result.items?.length) {
                const company = await CompaniesService.getCurrent();
                if (company?.gpsLocators?.length) {
                    const ids = result.items.filter(x => x.trackingDeviceId && x.id).map(x => x.id as string);
                    if (ids.length) {
                        const res = await TrackingService.getGpsVehiclesPosition(ids);
                        // Merge by vehicle ID to avoid race conditions
                        setTrackingVehicles(prev => {
                            if (!prev) return res;
                            const existingMap = new Map(prev.map(t => [t.vehicleId, t]));
                            res.forEach(t => existingMap.set(t.vehicleId, t));
                            return Array.from(existingMap.values());
                        });
                    }
                }
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get previous vehicles page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, hasGpsPolicy, addToast, t]);

    // Fetch all vehicles for Excel export
    const fetchAllForExport = useCallback(async () => {
        const result = await VehiclesService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    // Adjust scroll position after prepending items (bidirectional scroll)
    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [vehicles, isLoadingPrevious, adjustScrollAfterPrepend]);

    useEffect(() => {
        localStorage.setItem('vehiclesViewMode', viewMode);
        if (pageLoaded && criteria.page > 1) {
            setVehicles([]);
            setTrackingVehicles(null);
            resetScrollState();
            setCriteria(prev => ({ ...prev, page: 1 }));
        }
    }, [viewMode]);

    // Atualizar page size quando a largura da janela muda (apenas para grid view)
    useEffect(() => {
        if (viewMode === 'grid') {
            setCriteria(prev => {
                if (prev.itemsPerPage !== responsivePageSize) {
                    return { ...prev, itemsPerPage: responsivePageSize, page: 1 };
                }
                return prev;
            });
        }
    }, [responsivePageSize, viewMode]);

    // Note: scrollToVehicleId clearing and scroll restoration are handled by the useBidirectionalScroll hook

    useEffect(() => {
        if (!hasPrivacyStatusPolicy && !hasGpsPolicy) {
            setPrivacyStatusMap({});
            return;
        }
        const vehicleIds = vehicles.map(v => v.id).filter((id): id is string => !!id);
        if (!vehicleIds.length) {
            setPrivacyStatusMap({});
            return;
        }
        TrackingService.getPrivacyStatusBatch(vehicleIds, new Date().toISOString())
            .then(setPrivacyStatusMap)
            .catch(err => Logger.error(LOGGER_LOG_TYPE.VEHICLES, 'Couldn\'t get privacy statuses', err));
    }, [vehicles, hasPrivacyStatusPolicy, hasGpsPolicy]);

    useEffect(() => {
        if (skipNextFetch.current) {
            skipNextFetch.current = false;
            return;
        }
        // Determine if this is a "load more" operation or a fresh load
        // Load more: when page is greater than max page in range (appending to list)
        const isLoadMore = criteria.page > loadedPageRange.max && viewMode === 'list' && pageLoaded;
        if (pageLoaded) {
            getData(isLoadMore);
        } else {
            setPageLoaded(true);
            loadFilters();
        }
    }, [criteria]);

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem('VEHICLESFILTER');
        if (cachedFilters != null && !pageLoaded) {
            const parsed: Filters = JSON.parse(cachedFilters);

            // If returning to list view with saved scroll position
            if (parsed.scrollToVehicleId && viewMode === 'list') {
                // Use the hook to initiate restoration
                const startPage = initiateRestoration({
                    page: parsed.page || 1,
                    scrollToItemId: parsed.scrollToVehicleId,
                });

                // Clear scroll position from cache
                const cleanedFilters = { ...parsed, scrollToVehicleId: undefined, page: 1 };
                localStorage.setItem('VEHICLESFILTER', JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);
                updateTotalFilters(cleanedFilters);

                // Set criteria to start at the saved page, preserving responsive page size
                setCriteria(prev => ({
                    ...prev,
                    registrationNumber: parsed.vehicleName,
                    registrationDate: parsed.registrationDate,
                    segmentId: parsed.segmentId,
                    brandId: parsed.brandId,
                    modelId: parsed.modelId,
                    defaultDriverId: parsed.defaultDriverId,
                    vehicleId: parsed.vehicleId,
                    reservationAvailable: parsed.reservationAvailable,
                    hasGps: parsed.hasGps,
                    minCost: parsed.costAverage?.[0],
                    maxCost: parsed.costAverage?.[1],
                    minCostTotal: parsed.costAccumulated?.[0],
                    maxCostTotal: parsed.costAccumulated?.[1],
                    driverId: parsed.driverId,
                    costsStartDate: parsed.costsStartDate,
                    costsEndDate: parsed.costsEndDate,
                    immobilized: parsed.immobilized,
                    localIds: parsed.localIds,
                    inactives: parsed.inactives,
                    allIn: parsed.allIn,
                    tags: parsed.tags,
                    page: startPage,
                    // Preservar o itemsPerPage dinâmico (não sobrescrever com o default)
                }));
                return;
            }

            // Normal load without scroll restoration
            parsed.scrollToVehicleId = undefined;
            parsed.page = 1;
            setLoadedPageRange({ min: 1, max: 1 });
            localStorage.setItem('VEHICLESFILTER', JSON.stringify(parsed));
            onChangeFilters(parsed, true);
        } else {
            getData();
        }
    };

    const onChangeFilters = (f: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(f);

        if (!isOnLoad) {
            setVehicles([]);
            setTrackingVehicles(null);
            resetScrollState();
            setTotalItems(0); // Prevent loadMore from triggering before new data loads
        }

        criteria.registrationNumber = f.vehicleName;
        criteria.registrationDate = f.registrationDate;
        criteria.segmentId = f.segmentId;
        criteria.brandId = f.brandId;
        criteria.modelId = f.modelId;
        criteria.defaultDriverId = f.defaultDriverId;
        criteria.vehicleId = f.vehicleId;
        criteria.reservationAvailable = f.reservationAvailable;
        criteria.hasGps = f.hasGps;
        criteria.minCost = f.costAverage && f.costAverage[0] != undefined ? f.costAverage[0] : undefined;
        criteria.maxCost = f.costAverage && f.costAverage[1] != undefined ? f.costAverage[1] : undefined;
        criteria.minCostTotal = f.costAccumulated && f.costAccumulated[0] != undefined ? f.costAccumulated[0] : undefined;
        criteria.maxCostTotal = f.costAccumulated && f.costAccumulated[1] != undefined ? f.costAccumulated[1] : undefined;
        criteria.driverId = f.driverId;
        criteria.costsStartDate = f.costsStartDate;
        criteria.costsEndDate = f.costsEndDate;
        criteria.immobilized = f.immobilized;
        criteria.localIds = f.localIds;
        criteria.localOperator = f.localOperator;
        criteria.inactives = f.inactives;
        criteria.allIn = f.allIn;
        criteria.tags = f.tags;
        criteria.page = !isOnLoad ? 1 : (f.page ?? 1);

        saveFiltersOnCache(f);
        setCriteria(prev => ({ ...prev, ...criteria }));
    };

    const updateTotalFilters = (f: Filters) => {
        let total = 0;

        if (f.vehicleId && f.vehicleId.length > 0) total++;
        if (f.registrationDate) total++;
        if (f.segmentId && f.segmentId.length > 0) total++;
        if (f.brandId && f.brandId.length > 0) total++;
        if (f.modelId && f.modelId.length > 0) total++;
        if (f.defaultDriverId && f.defaultDriverId.length > 0) total++;
        if (f.reservationAvailable) total++;
        if (f.hasGps) total++;
        if (f.driverId && f.driverId.length > 0) total++;
        if (f.costAverage && (f.costAverage[0] != undefined || f.costAverage[1] != undefined)) total++;
        if (f.costAccumulated && (f.costAccumulated[0] != undefined || f.costAccumulated[1] != undefined)) total++;
        if (f.costsStartDate || f.costsEndDate) total++;
        if (f.immobilized) total++;
        if (f.inactives) total++;
        if (f.localIds?.length) total++;

        setFiltersTotal(total);
    };

    const hydrateTracking = (v: VehicleViewModel) => {
        const trackingVehicle = trackingVehicles?.find(tv => tv.licensePlate === v.registrationNumber || tv.id === v.trackingDeviceId);
        if (trackingVehicle) {
            v.ignition = trackingVehicle.ignition;
            v.latitude = trackingVehicle.latitude;
            v.longitude = trackingVehicle.longitude;
            v.movement = trackingVehicle.movement;
            v.date = trackingVehicle.date;
            v.address = trackingVehicle.address;
            v.lastTripId = trackingVehicle.lastTripId;
            v.gpsDataHydrated = true;
        } else if (trackingVehicles || !hasGpsPolicy) {
            v.gpsDataHydrated = true;
        }
        return v;
    };

    const renderVehiclesCard = () => {
        return (
            <div className={styles.containerCards} ref={cardsContainerRef}>
                {vehicles.map((v, i) => {
                    const vehicle = hydrateTracking(v);
                    return (
                        <VehicleCard
                            vehicle={vehicle}
                            hasGpsPolicy={hasGpsPolicy && companyHasGpsLocators}
                            hasCountersPolicy={hasCountersPolicy}
                            privacyStatus={v.id ? privacyStatusMap[v.id] : undefined}
                            key={`vehicle_${v.id}_${i}`}
                            onCardPress={onCardClick}
                            onHref={row => `/vehicles/details/${row.id}`}
                        />
                    );
                })}
            </div>
        );
    };

    const renderWithoutVehicles = () => {
        return (companyHaveVehicles != false || !hasVehiclesWritePolicy) ? (
            <div className={styles.containerCards} style={{ justifyContent: 'center' }}>
                {t('common.no_results')}
            </div>
        ) : renderStartUsingFleetMax();
    };

    const renderStartUsingFleetMax = () => {
        return (
            <div className={styles.containerCards} style={{ justifyContent: 'center' }}>
                {t('common.start_using_fleetmax')} <ScreenHeaderButton id="five-step" icon={AddIcon} onClick={() => navigateTo('new')} className={styles.buttonAdd} />
            </div>
        );
    };

    const handleSort = (column: string, isAsc: boolean) => {
        setVehicles([]);
        setTrackingVehicles(null);
        resetScrollState();
        setTotalItems(0); // Prevent loadMore from triggering before new data loads
        setCriteria(prev => ({
            ...prev,
            orderColumn: column,
            orderBy: isAsc ? 'asc' : 'desc',
            page: 1
        }));
    };

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setVehicles([]);
        setTrackingVehicles(null);
        resetScrollState();
        setTotalItems(0); // Prevent loadMore from triggering before new data loads
        filters.allIn = value || undefined;
        filters.tags = undefined;
        saveFiltersOnCache(filters);
        setCriteria({ ...criteria, page: 1, allIn: value || undefined, tags: undefined });
    }, 500);

    const JumpToImport = () => {
        history.push(`/import-data`);
    };

    // Build filter badge items
    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.costsStartDate || filters.costsEndDate) {
            const value = filters.costsStartDate && filters.costsEndDate
                ? <>{t('common.range.from')} <DateFormat format={DATE_FORMAT_MONTH_YEAR} value={filters.costsStartDate} /> {t('common.range.to').toLowerCase()} <DateFormat format={DATE_FORMAT_MONTH_YEAR} value={filters.costsEndDate} /></>
                : filters.costsStartDate
                    ? <>{t('common.range.from')} <DateFormat format={DATE_FORMAT_MONTH_YEAR} value={filters.costsStartDate} /></>
                    : <>{t('common.range.to')} <DateFormat format={DATE_FORMAT_MONTH_YEAR} value={filters.costsEndDate!} /></>;
            items.push({
                key: 'costsDate',
                label: t('vehicles.list.total_monthly_cost_date') as string,
                value,
                onRemove: () => { filters.costsStartDate = filters.costsEndDate = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.vehicleName) {
            items.push({
                key: 'vehicle',
                label: t('vehicle.vehicle') as string,
                value: filters.vehicleName,
                onRemove: () => { filters.vehicleId = filters.vehicleName = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.segmentName) {
            items.push({
                key: 'segment',
                label: t('vehicle.type') as string,
                value: filters.segmentName,
                onRemove: () => { filters.segmentId = filters.segmentName = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.brandName) {
            items.push({
                key: 'brand',
                label: t('vehicle.brand') as string,
                value: filters.brandName,
                onRemove: () => { filters.brandId = filters.brandName = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.modelName) {
            items.push({
                key: 'model',
                label: t('vehicle.model') as string,
                value: filters.modelName,
                onRemove: () => { filters.modelId = filters.modelName = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.reservationAvailable) {
            items.push({
                key: 'reservation',
                label: t('vehicle.available_for_reservation') as string,
                value: t('common.yes') as string,
                onRemove: () => { filters.reservationAvailable = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.hasGps) {
            items.push({
                key: 'hasGps',
                label: t('vehicle.has_gps') as string,
                value: t('common.yes') as string,
                onRemove: () => { filters.hasGps = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.defaultDriverName) {
            items.push({
                key: 'defaultDriver',
                label: t('vehicle.default_driver') as string,
                value: filters.defaultDriverName,
                onRemove: () => { filters.defaultDriverId = filters.defaultDriverName = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.driverName) {
            items.push({
                key: 'driver',
                label: t('common.state') as string,
                value: filters.driverName,
                onRemove: () => { filters.driverId = filters.driverName = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.costAverage?.filter(x => x !== undefined).length) {
            const value = filters.costAverage.filter(i => i !== undefined).length === 2
                ? <>{t('common.between')} <MoneyFormat value={filters.costAverage[0]} /> {t('common.and')} <MoneyFormat value={filters.costAverage[1]} /></>
                : filters.costAverage[0] !== undefined
                    ? <>{t('common.above')} <MoneyFormat value={filters.costAverage[0]} /></>
                    : <>{t('common.below')} <MoneyFormat value={filters.costAverage[1]} /></>;
            items.push({
                key: 'costAverage',
                label: t('vehicles.list.average_monthly_cost') as string,
                value,
                onRemove: () => { filters.costAverage = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.costAccumulated?.filter(x => x !== undefined).length) {
            const value = filters.costAccumulated.filter(i => i !== undefined).length === 2
                ? <>{t('common.between')} <MoneyFormat value={filters.costAccumulated[0]} /> {t('common.and')} <MoneyFormat value={filters.costAccumulated[1]} /></>
                : filters.costAccumulated[0] !== undefined
                    ? <>{t('common.above')} <MoneyFormat value={filters.costAccumulated[0]} /></>
                    : <>{t('common.below')} <MoneyFormat value={filters.costAccumulated[1]} /></>;
            items.push({
                key: 'costAccumulated',
                label: t('vehicles.list.total_monthly_cost') as string,
                value,
                onRemove: () => { filters.costAccumulated = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.immobilized) {
            items.push({
                key: 'immobilized',
                label: t('vehicle.immobilized') as string,
                value: t('common.yes') as string,
                onRemove: () => { filters.immobilized = undefined; onChangeFilters(filters); }
            });
        }

        if (filters.localNames?.length) {
            items.push({
                key: 'local',
                label: `${t('vehicle.local')} (${filters.localOperator === 'AND' ? t('common.filter_all' as any) : t('common.filter_any' as any)})` as string,
                value: filters.localNames.join(', '),
                onRemove: () => { filters.localIds = []; filters.localNames = []; onChangeFilters(filters); }
            });
        }

        if (filters.inactives) {
            items.push({
                key: 'inactives',
                label: t('vehicle.inactives') as string,
                value: t('common.yes') as string,
                onRemove: () => { filters.inactives = false; onChangeFilters(filters); }
            });
        }

        return items;
    }, [filters, t, userProfile]);

    return (
        <ScreenTitle title={t('vehicles.title')}>
            <CompactPageLayout
                title={t('vehicles.title') as string}
                pageWrapperClassName={styles.pageWrapper}
                headerClassName={styles.compactHeader}
                headerButtonsClassName={styles.headerButtons}
                contentAreaClassName={styles.contentArea}
                middleContent={
                    <div className={styles.middleContent}>
                        <FilterBadgesOverflow items={filterItems} align="left" />
                        <div className={styles.searchInputs} id="second-step">
                            <InputSearch
                                inputContainerClassName={styles.inputSearchContainerCustomization}
                                onChangeValue={debounced}
                                placeholder={t('vehicles.list.search_placeholder')}
                                defaultValue={criteria.allIn}
                            />
                        </div>
                    </div>
                }
                headerButtons={
                    <>
                        <Popover
                            contentContainerClassName={styles.filtersPopoverContainer}
                            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                            onClickOutside={() => updateTotalFilters(filters)}
                            content={setIsPopoverOpen => (
                                <VehicleFiltersScreen
                                    filters={filters}
                                    hasCountersPolicy={hasCountersPolicy}
                                    onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f); }}
                                    onChange={f => updateTotalFilters(f)}
                                />
                            )}
                        >
                            {(isPopoverOpen, setIsPopoverOpen) => (
                                <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)} id="third-step">
                                    {filtersTotal > 0 && (
                                        <div className={styles.counterList}><div className={styles.counterNumber}>{filtersTotal}</div></div>
                                    )}
                                </ScreenHeaderButton>
                            )}
                        </Popover>

                        {viewMode === 'list' && (
                            <ExportListingButton exportRef={exportListingRef} />
                        )}

                        {hasImportsWritePolicy && userProfile?.hasExcelImportData && (
                            <div data-tip={t('import_data.title')}>
                                <ScreenHeaderButton id="four-step" className={styles.emptyBorder} classNameIcon={styles.importIcon} icon={ImportIcon} onClick={() => JumpToImport()} />
                            </div>
                        )}
                        {hasVehiclesWritePolicy && <ScreenHeaderButton id="five-step" icon={AddIcon} onClick={() => navigateTo('new')} />}
                        <ScreenHeaderButton icon={InfoIcon} onClick={() => setTourOpen(true)} className={styles.infoIcon} />
                    </>
                }
                afterContent={
                    <button
                        className={styles.floatingViewToggle}
                        onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                        title={viewMode === 'grid' ? t('common.list_view') as string : t('common.grid_view') as string}
                    >
                        {viewMode === 'grid' ? <FaList /> : <FaTh />}
                    </button>
                }
            >
                <div id="first-step" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {viewMode === 'grid' ? (
                        <Box>
                            {!!vehicles.length ? renderVehiclesCard() : renderWithoutVehicles()}
                            <PaginationWithInfo
                                itemName={t('vehicles.list.vehicles')}
                                currentPage={criteria.page}
                                itemsPerPage={criteria.itemsPerPage}
                                pageItems={vehicles.length}
                                totalItems={totalItems}
                                onChange={onPageChange}
                            />
                        </Box>
                    ) : (
                        !!vehicles.length ? (
                            <VehicleListTable
                                vehicles={vehicles}
                                hasCountersPolicy={hasCountersPolicy}
                                hasGpsPolicy={hasGpsPolicy && companyHasGpsLocators}
                                hasAlertsReadPolicy={hasAlertsReadPolicy}
                                trackingVehicles={trackingVehicles}
                                privacyStatusMap={privacyStatusMap}
                                isLoadingMore={isLoadingMore}
                                hasMore={getHasMore(vehicles.length, totalItems)}
                                onLoadMore={getOnLoadMore(loadMore)}
                                onClickItem={(vehicle, event) => onCardClick(event, vehicle.id)}
                                onOrderChange={handleSort}
                                criteria={criteria}
                                scrollToRowId={scrollToVehicleId}
                                hasMorePrevious={getHasMorePrevious()}
                                isLoadingPrevious={isLoadingPrevious}
                                onLoadPrevious={getOnLoadPrevious(loadPrevious)}
                                onFetchAllForExport={fetchAllForExport}
                                exportRef={exportListingRef}
                            />
                        ) : (
                            <Box>{renderWithoutVehicles()}</Box>
                        )
                    )}
                </div>
            </CompactPageLayout>

            <InfoTour
                steps={steps.map(x => ({
                    selector: x.selector,
                    content: <HelperModal title={x.title} text={x.text} />,
                    position: x.position
                }))}
                isOpen={tourOpen}
                onRequestClose={() => setTourOpen(false)}
            />
        </ScreenTitle>
    );
};

export default VehiclesScreen;
